// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import api from './advisorApi';
import type {
  Relationship,
  RelationshipCreateDTO,
  RelationshipUpdateDTO,
  RelationshipReassignDTO,
  RelationshipTypeChangeDTO,
  RelationshipTypeCompatibility,
  PageResponse,
  RelationshipHealthStats,
  RelationshipAggregate,
  DashboardStats
} from '../types/relationship';

export interface RelationshipListFilters {
  page?: number;
  size?: number;
  search?: string;
  derivedStatus?: string;
  notInvited?: boolean;
  noPolicies?: boolean;
  dataAvailable?: boolean;
  loaRejected?: boolean;
  all?: boolean;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  /** Central-taxonomy tag ids — ANY-of filter (Tags PRD FR-13). */
  tagIds?: string[];
}

export const relationshipApi = {
  /**
   * Get paginated relationship list with optional filters
   */
  getRelationships: async (filters: RelationshipListFilters = {}): Promise<PageResponse<Relationship>> => {
    const params: Record<string, string | number | boolean | undefined> = {
      page: filters.page ?? 0,
      size: filters.size ?? 20,
    };

    if (filters.search) params.search = filters.search;
    if (filters.derivedStatus) params.derivedStatus = filters.derivedStatus;
    if (filters.notInvited) params.notInvited = true;
    if (filters.noPolicies) params.noPolicies = true;
    if (filters.dataAvailable) params.dataAvailable = true;
    if (filters.loaRejected) params.loaRejected = true;
    if (filters.all) params.all = true;
    if (filters.sortBy) params.sort = `${filters.sortBy},${filters.sortDirection || 'asc'}`;

    const response = await api.get<PageResponse<Relationship>>('/profiles', {
      params: {
        ...params,
        tagIds: filters.tagIds?.length ? filters.tagIds : undefined,
      },
      // Spring binds List<UUID> from repeated query params (tagIds=a&tagIds=b) —
      // same fix as requestOverviewApi.list / fundsApi.search.
      paramsSerializer: { indexes: null },
    });
    return response.data;
  },

  /**
   * Get all relationships across the brokerage (admin only)
   */
  getAllRelationships: async (page: number = 0, size: number = 20): Promise<PageResponse<Relationship>> => {
    const response = await api.get<PageResponse<Relationship>>('/profiles', {
      params: { page, size, all: true }
    });
    return response.data;
  },

  /**
   * Get relationships with zero policies (for dashboard overview)
   */
  getRelationshipsWithoutPolicies: async (size: number = 5): Promise<PageResponse<Relationship>> => {
    const response = await api.get<PageResponse<Relationship>>('/profiles', {
      params: { page: 0, size, noPolicies: true, sort: 'createdAt,desc' }
    });
    return response.data;
  },

  /**
   * Get single relationship by ID
   */
  getRelationship: async (id: string): Promise<Relationship> => {
    const response = await api.get<Relationship>(`/profiles/${id}`);
    return response.data;
  },

  /**
   * Create new relationship (automatically assigned to current advisor)
   */
  createRelationship: async (data: RelationshipCreateDTO): Promise<Relationship> => {
    const response = await api.post<Relationship>('/profiles', data);
    return response.data;
  },

  /**
   * Update relationship
   */
  updateRelationship: async (id: string, data: RelationshipUpdateDTO): Promise<Relationship> => {
    const response = await api.put<Relationship>(`/profiles/${id}`, {
      profileName: data.relationshipName,
      crmRelationshipNumber: data.crmRelationshipNumber,
      notes: data.notes,
      status: data.status,
    });
    return response.data;
  },

  /**
   * Reassign relationship to different advisor (admin only)
   */
  reassignRelationship: async (id: string, data: RelationshipReassignDTO): Promise<Relationship> => {
    const response = await api.patch<Relationship>(`/profiles/${id}/reassign`, data);
    return response.data;
  },

  /**
   * Delete (archive) relationship
   */
  deleteRelationship: async (id: string): Promise<void> => {
    await api.delete(`/profiles/${id}`);
  },

  /**
   * Search relationship by CRM relationship number
   */
  searchByCrmRelationshipNumber: async (crmRelationshipNumber: string): Promise<Relationship> => {
    const response = await api.get<Relationship>('/profiles/search/crm', {
      params: { crmRelationshipNumber }
    });
    return response.data;
  },

  /**
   * Change relationship type (e.g., SOLO -> JOINT)
   */
  changeRelationshipType: async (id: string, data: RelationshipTypeChangeDTO): Promise<Relationship> => {
    // Backend ProfileTypeChangeDTO expects `newProfileType` (mirrors updateRelationship's
    // relationshipName -> profileName mapping).
    const response = await api.put<Relationship>(`/profiles/${id}/type`, {
      newProfileType: data.newRelationshipType,
      reason: data.reason,
    });
    return response.data;
  },

  /**
   * Check if relationship type change is compatible with current client count
   */
  checkRelationshipTypeCompatibility: async (id: string, targetType: string): Promise<RelationshipTypeCompatibility> => {
    const response = await api.get<RelationshipTypeCompatibility>(`/profiles/${id}/type-compatibility`, {
      params: { targetType }
    });
    return response.data;
  },

  /**
   * Get relationship type distribution statistics
   */
  getRelationshipTypeStats: async (): Promise<Record<string, number>> => {
    const response = await api.get<Record<string, number>>('/profiles/stats/types');
    return response.data;
  },

  /**
   * Get relationship health statistics (derived status counts)
   */
  getRelationshipHealthStats: async (): Promise<RelationshipHealthStats> => {
    const response = await api.get<RelationshipHealthStats>('/profiles/health-stats');
    return response.data;
  },

  /**
   * Get relationship aggregate with derived status and health indicators
   */
  getRelationshipAggregate: async (relationshipId: string): Promise<RelationshipAggregate> => {
    const response = await api.get<RelationshipAggregate>(`/profiles/${relationshipId}/aggregate`);
    return response.data;
  },

  /**
   * Get dashboard statistics (clients, relationships, LOAs)
   */
  getDashboardStats: async (): Promise<DashboardStats> => {
    const response = await api.get<DashboardStats>('/profiles/dashboard-stats');
    return response.data;
  },
};

// Backward compatibility alias
export const profileApi = relationshipApi;
